import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { shallowEqual, type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { advancedMapLayersReducer, type AdvancedMapLayersState } from '@alltrails/advanced-map-layers/redux/advancedMapLayersSlice';
import amplitudeAnalyticsSettings from '@alltrails/amplitude/slices/amplitudeAnalyticsSettings';
import conditionsMiddleware from '@alltrails/conditions/redux/conditionsMiddleware';
import { conditionsReducer, ConditionsState } from '@alltrails/conditions/redux/conditionsSlice';
import context from '@alltrails/context/slices/contextSlice';
import currencyCode from '@alltrails/context/slices/currencyCodeSlice';
import plans from '@alltrails/context/slices/plansSlice';
import type RequiredPreloadedState from '@alltrails/core/types/RequiredPreloadedState';
import experiments from '@alltrails/experiments/slices/experiments';
import { exploreServiceReducer, type ExploreServiceState } from '@alltrails/explore-service/redux/exploreServiceSlice';
import features from '@alltrails/features/slices/features';
import { flyoverReducer, type FlyoverState } from '@alltrails/flyover/redux/flyover';
import { heatmapsReducer, HeatmapsState } from '@alltrails/heatmaps/redux/heatmapsSlice';
import lists, { type ListsState } from '@alltrails/lists/slices/lists';
import { customRouteReducer, CustomRouteState } from '@alltrails/maps/redux/customRoute';
import { mapReducer, type MapState } from '@alltrails/maps/redux/reducer';
import trailheadReducer, { type TrailheadUiState } from '@alltrails/maps/redux/trailhead';
import { photoTourReducer, type PhotoTourState } from '@alltrails/photo-tour/redux/photoTourSlice';
import { baseApi } from '@alltrails/redux-helpers/baseApi';
import algoliaConfigs from '@alltrails/search/slices/algoliaConfigs';
import userConnectionLists from '@alltrails/social-connections/slices/userConnectionLists';
import userConnections from '@alltrails/social-connections/slices/userConnections';
import { AlgoliaConfigs } from '@alltrails/types/algoliaConfigs';
import type Context from '@alltrails/types/Context';
import type CountryCode from '@alltrails/types/CountryCode';
import upsellContent from '@alltrails/upsell/slices/upsellContent';
import { listenerMiddleware } from './listenerMiddleware';
import advancedBuilderMeasure, { type AdvancedBuilderMeasureState } from './slices/advancedBuilderMeasureSlice';
import advancedBuilder, { type AdvancedBuilderState } from './slices/advancedBuilderSlice';
import ask, { type AskState } from './slices/askSlice';
import exploreCustomRoute, { type ExploreCustomRouteState } from './slices/customRouteSlice';
import explore, { type ExploreState } from './slices/exploreSlice';
import explorePOI, { type ExplorePOIState } from './slices/poiSlice';
import tripPlanner, { type TripPlannerState } from './slices/tripPlannerSlice';

export type PreloadedState = RequiredPreloadedState & {
  algoliaConfigs: AlgoliaConfigs;
  ask?: AskState;
  conditions: ConditionsState;
  explore: ExploreState;
  exploreService: ExploreServiceState;
  flyover: FlyoverState;
  lists: ListsState;
  map: MapState;
  customRoute: CustomRouteState;
  exploreCustomRoute: ExploreCustomRouteState;
  poi: ExplorePOIState;
  heatmaps: HeatmapsState;
  photoTour: PhotoTourState;
  advancedMapLayers: AdvancedMapLayersState;
  trailhead: TrailheadUiState;
  advancedBuilder?: AdvancedBuilderState;
  advancedBuilderMeasure?: AdvancedBuilderMeasureState;
  tripPlanner?: TripPlannerState;
};

export const reducer = combineReducers({
  algoliaConfigs,
  amplitudeAnalyticsSettings,
  ask,
  [baseApi.reducerPath]: baseApi.reducer,
  browser: (state = {}) => state,
  conditions: conditionsReducer,
  context,
  currencyCode,
  experiments,
  explore,
  explorePOI,
  exploreService: exploreServiceReducer,
  features,
  flyover: flyoverReducer,
  irclickid: (state = '') => state,
  lists,
  map: mapReducer,
  customRoute: customRouteReducer,
  exploreCustomRoute,
  plans,
  heatmaps: heatmapsReducer,
  photoTour: photoTourReducer,
  advancedMapLayers: advancedMapLayersReducer,
  trailhead: trailheadReducer,
  advancedBuilder,
  advancedBuilderMeasure,
  tripPlanner,
  requestCountryCode: (state = '' as CountryCode) => state,
  upsellContent,
  userConnectionLists,
  userConnections
});

export const buildStore = (preloadedState?: Partial<PreloadedState>) =>
  configureStore({
    reducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({ immutableCheck: false }).prepend(listenerMiddleware.middleware).concat(baseApi.middleware).concat(conditionsMiddleware)
  });

export type AppStore = ReturnType<typeof buildStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']> & { context?: Context };

// these are intended to use within the app only
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = (selector: (state: RootState) => any) =>
  useSelector<RootState, any>(selector, shallowEqual);
